import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { cloneAndScaleUV, setupTextureRepeat } from '../../utils/geometryUltis'
import scaleAnimationPositions from '../../utils/scaleAnimationKeyFrames'
import useGateBoundingDataChecker from '../../utils/gateBoundingDataChecker'
import DoorSingle from '../Models/Door-Single'

// ✅ Helper do skalowania geometrii — poza komponentem
function scaleGateGeometry(sourceGeometry, gateHeight, gateWidth) {
  return cloneAndScaleUV(
    sourceGeometry,
    1,
    gateHeight / 100,
    gateWidth / 100,
    gateWidth / 100,
    gateHeight / 100
  )
}

// ✅ shader: wycina "dziurę" w materiale bramy na podstawie prostokątów drzwi (max 2)
const attachDoorCutoutShader = (mat) => {
  mat.userData.doorCutout = mat.userData.doorCutout || {
    count: 0,
    centers: [new THREE.Vector3(), new THREE.Vector3()], // world centers
    half: [new THREE.Vector2(), new THREE.Vector2()], // half sizes (y,z) in meters
  }

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uDoorCount = { value: mat.userData.doorCutout.count }
    shader.uniforms.uDoorCenter0 = { value: mat.userData.doorCutout.centers[0] }
    shader.uniforms.uDoorCenter1 = { value: mat.userData.doorCutout.centers[1] }
    shader.uniforms.uDoorHalf0 = { value: mat.userData.doorCutout.half[0] }
    shader.uniforms.uDoorHalf1 = { value: mat.userData.doorCutout.half[1] }

    shader.vertexShader = shader.vertexShader.replace(
      'void main() {',
      `
        varying vec3 vWorldPos;
        void main() {
      `
    )

    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `
        #include <begin_vertex>
        vec4 wp = modelMatrix * vec4(transformed, 1.0);
        vWorldPos = wp.xyz;
      `
    )

    shader.fragmentShader = shader.fragmentShader.replace(
      'void main() {',
      `
        varying vec3 vWorldPos;
        uniform int uDoorCount;
        uniform vec3 uDoorCenter0;
        uniform vec3 uDoorCenter1;
        uniform vec2 uDoorHalf0;
        uniform vec2 uDoorHalf1;

        bool insideRect(vec3 c, vec2 h){
          return (abs(vWorldPos.y - c.y) <= h.x) && (abs(vWorldPos.z - c.z) <= h.y);
        }

        void main() {
          if (uDoorCount > 0) {
            if (insideRect(uDoorCenter0, uDoorHalf0)) discard;
          }
          if (uDoorCount > 1) {
            if (insideRect(uDoorCenter1, uDoorHalf1)) discard;
          }
      `
    )

    mat.userData.shader = shader
  }

  mat.customProgramCacheKey = () => 'gate_door_cutout_v1'
  mat.needsUpdate = true
}

const DrzwiFramuga = forwardRef(({ gate, onUpdate, textures, doorsData, ...props }, ref) => {
  // ✅ tylko to liczymy do bbox/cutout ścian
  const gateMeshRef = useRef()
  const wrapperRef = useRef()

  const { nodes, materials, animations } = useGLTF('/models/gates/Brama-dwuskrzydlowa-2.glb')

  const [isOpen, setIsOpen] = useState(false)
  const isOpenRef = useRef(false)
  const isAnimatingRef = useRef(false)
  const mixerRef = useRef(null)

  // ✅ stała odległość od krawędzi bramy (20 cm)
  const EDGE_OFFSET_M = 0.2

  // ✅ drzwi przypisane do tej bramy (front_wall)
  const doors = useMemo(() => {
    const list = doorsData?.front_wall || []
    return list.filter((d) => d?.gateId === gate?.id)
  }, [doorsData, gate?.id])

  const hasDoorsInGate = doors.length > 0

  // ✅ ref na zewnątrz: tylko brama
  useImperativeHandle(ref, () => gateMeshRef.current)

  // ✅ używamy TYLKO gateSide: 'left' | 'right'
  const resolveDoorSide = useCallback((door) => {
    return String(door?.gateSide || 'left').toLowerCase() === 'right' ? 2 : 1
  }, [])

  // ==========================================================
  // ✅ FIX #1: stabilne "zamknięcie" animacji na klatce 0
  // ==========================================================
  const freezeClosedPose = useCallback(() => {
    if (!mixerRef.current) return
    if (!gateMeshRef.current) return

    const mixer = mixerRef.current
    mixer.stopAllAction()

    ;(mixer._actions || []).forEach((action) => {
      action.reset()
      action.enabled = true
      action.clampWhenFinished = true
      action.setLoop(THREE.LoopOnce, 1)
      action.time = 0
      action.timeScale = 0
      action.paused = false
      action.play()
      action.paused = true
    })

    mixer.update(0)

    isOpenRef.current = false
    isAnimatingRef.current = false
    setIsOpen(false)
  }, [])

  // ===== Animations =====
  const scaledAnimations = useMemo(() => {
    const scale = new THREE.Vector3(1, gate.height / 100, gate.width / 100)
    return (animations || []).map((clip) => scaleAnimationPositions(clip, scale))
  }, [animations, gate.height, gate.width])

  useEffect(() => {
    if (!gateMeshRef.current) return
    if (!scaledAnimations || scaledAnimations.length === 0) return

    if (mixerRef.current) {
      mixerRef.current.stopAllAction()
      mixerRef.current.uncacheRoot(gateMeshRef.current)
    }

    mixerRef.current = new THREE.AnimationMixer(gateMeshRef.current)

    scaledAnimations.forEach((clip) => {
      const action = mixerRef.current.clipAction(clip)
      action.clampWhenFinished = true
      action.enabled = true
      action.paused = true
      action.timeScale = 0
      action.time = 0
      action.play()
      action.paused = true
    })

    freezeClosedPose()
  }, [scaledAnimations, freezeClosedPose])

  // ✅ Animation loop
  useEffect(() => {
    let frameId
    const animate = () => {
      mixerRef.current?.update(1 / 60)
      frameId = requestAnimationFrame(animate)
    }
    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [])

  // ✅ jeśli są drzwi -> zawsze brama "zamknięta" i brak animacji
  useEffect(() => {
    if (!hasDoorsInGate) return
    freezeClosedPose()
  }, [hasDoorsInGate, freezeClosedPose])

  // ==========================================================
  // ✅ FIX #2: resetuj po zmianie wymiarów i pozycji
  // ==========================================================
  useEffect(() => {
    if (!mixerRef.current) return
    if (!scaledAnimations || scaledAnimations.length === 0) return
    freezeClosedPose()
  }, [
    gate.position?.x,
    gate.position?.y,
    gate.position?.z,
    gate.width,
    gate.height,
    scaledAnimations,
    freezeClosedPose,
  ])

  // ==========================================================
  // ✅ FIX #3: bbox/wycięcie ściany liczymy tylko z bramy
  // ==========================================================
  const lastCutSigRef = useRef('')

  const handleBoundingDataChanged = useCallback(() => {
    if (isAnimatingRef.current) return
    if (!gateMeshRef.current) return
    if (typeof onUpdate !== 'function') return

    const sig = [
      props.length,
      gate.width,
      gate.height,
      gate.position?.x,
      gate.position?.y,
      gate.position?.z,
    ].join('|')

    if (gate?.closedBoundingBox && gate?.positionAdjusted && lastCutSigRef.current === sig) return
    if (isOpenRef.current) return

    freezeClosedPose()

    const box = new THREE.Box3().setFromObject(gateMeshRef.current)
    const size = new THREE.Vector3()
    box.getSize(size)

    const updateData = { boundingData: { box, size } }

    const x = (-props.length / 100) / 2 + 0.5
    const z = gate.position.z
    const adjustedY = gate.height / 100 / 2

    updateData.closedBoundingBox = { box: box.clone(), size: size.clone() }
    updateData.positionAdjusted = [x - 0.5, adjustedY, z]

    lastCutSigRef.current = sig
    onUpdate(updateData)
  }, [
    onUpdate,
    props.length,
    gate.width,
    gate.height,
    gate.position?.x,
    gate.position?.y,
    gate.position?.z,
    gate?.closedBoundingBox,
    gate?.positionAdjusted,
    freezeClosedPose,
  ])

  useGateBoundingDataChecker({
    nodes,
    gate,
    onBoundingDataChanged: handleBoundingDataChanged,
  })

  // ===== Scaled geometries =====
  const scaledDoorFrameGeometry = useMemo(
    () => scaleGateGeometry(nodes.Door_Frame.geometry, gate.height, gate.width),
    [nodes.Door_Frame.geometry, gate.height, gate.width]
  )

  const scaledDoorLeftGeometry = useMemo(
    () => scaleGateGeometry(nodes.Door_Left.geometry, gate.height, gate.width),
    [nodes.Door_Left.geometry, gate.height, gate.width]
  )

  const scaledMesh003Geometry = useMemo(
    () => scaleGateGeometry(nodes.Mesh003.geometry, gate.height, gate.width),
    [nodes.Mesh003.geometry, gate.height, gate.width]
  )

  const scaledMesh0031Geometry = useMemo(
    () => scaleGateGeometry(nodes.Mesh003_1.geometry, gate.height, gate.width),
    [nodes.Mesh003_1.geometry, gate.height, gate.width]
  )

  // ===== Click handler =====
  const handleClick = useCallback(() => {
    // ✅ jeśli są drzwi -> wyłącz animacje
    if (hasDoorsInGate) return

    if (!mixerRef.current || !scaledAnimations || scaledAnimations.length === 0) return

    const clip = scaledAnimations[0]
    const action = mixerRef.current.clipAction(clip)

    action.reset()
    action.setLoop(THREE.LoopOnce, 1)
    action.clampWhenFinished = true
    action.enabled = true
    action.paused = false

    isAnimatingRef.current = true

    const onFinished = () => {
      isAnimatingRef.current = false
      mixerRef.current?.removeEventListener('finished', onFinished)
    }

    mixerRef.current.addEventListener('finished', onFinished)

    if (!isOpenRef.current) {
      action.time = 0
      action.timeScale = 1
      action.play()
    } else {
      action.time = clip.duration
      action.timeScale = -1
      action.play()
    }

    isOpenRef.current = !isOpenRef.current
    setIsOpen((prev) => !prev)
  }, [scaledAnimations, hasDoorsInGate])

  // ===== Gate material (skrzydła) + wycinanie drzwi =====
  const gateMaterial = useMemo(() => {
    const base = materials?.Material ?? materials?.['Dark metal.001']
    const gateColor = textures?.gates_double_leaf_color
    if (!base || !gateColor) return undefined

    const newMaterial = base.clone()
    newMaterial.side = THREE.DoubleSide

    const repeatX = gate.width / 100 / 2 / 2 / gateColor.multiplierHorizontal
    const repeatY = 1 / gateColor.multiplierVertical

    newMaterial.map = gateColor.textureDiff ?? null
    newMaterial.metalnessMap = gateColor.textureMetalness ?? null
    newMaterial.normalMap = gateColor.textureNormalness ?? null

    if (newMaterial.map) setupTextureRepeat(newMaterial.map, repeatX, repeatY)
    if (newMaterial.metalnessMap) setupTextureRepeat(newMaterial.metalnessMap, repeatX, repeatY)
    if (newMaterial.normalMap) setupTextureRepeat(newMaterial.normalMap, repeatX, repeatY)

    newMaterial.metalness = 1
    newMaterial.roughness = 0.5
    newMaterial.color = new THREE.Color(gateColor.hex)
    newMaterial.needsUpdate = true

    attachDoorCutoutShader(newMaterial)
    return newMaterial
  }, [materials, gate.width, textures])

  useEffect(() => {
    return () => {
      gateMaterial?.dispose?.()
    }
  }, [gateMaterial])

  const darkMetalCutMaterial = useMemo(() => {
    const base = materials?.['Dark metal.001'] ?? materials?.Material
    if (!base) return undefined
    const m = base.clone()
    m.side = THREE.DoubleSide
    attachDoorCutoutShader(m)
    return m
  }, [materials])

  useEffect(() => {
    return () => {
      darkMetalCutMaterial?.dispose?.()
    }
  }, [darkMetalCutMaterial])

  // ===== Precomputed scales =====
  const gateHeightScale = gate.height / 100
  const gateWidthScale = gate.width / 100
  const fallbackMaterial = materials?.['Dark metal.001'] ?? materials?.Material

  // ==========================================================
  // ✅ prostokąty drzwi w WORLD Y/Z (max 2) - gateSide only
  // ==========================================================
  const doorRects = useMemo(() => {
    const list = (doors || []).slice(0, 2)
    const gateWorldZ = gate.position?.z ?? 0
    const gateWm = (gate.width || 0) / 100

    return list.map((d) => {
      const sideNum = resolveDoorSide(d)
      const doorWm = (Number(d.width || d.size || 0) || 0) / 100
      const doorHm = (Number(d.height || 0) || 0) / 100

      const centerOffset = Math.max(0, gateWm / 2 - EDGE_OFFSET_M - doorWm / 2)
      const centerZ = gateWorldZ + (sideNum === 2 ? +centerOffset : -centerOffset)
      const centerY = doorHm / 2

      return {
        center: new THREE.Vector3(0, centerY, centerZ),
        half: new THREE.Vector2(doorHm / 2, doorWm / 2),
      }
    })
  }, [doors, gate.width, gate.position?.z, resolveDoorSide])

  // ✅ uniformy (skrzydła)
  useEffect(() => {
    if (!gateMaterial?.userData?.doorCutout) return

    const cut = gateMaterial.userData.doorCutout
    cut.count = doorRects.length

    cut.centers[0].set(0, -9999, -9999)
    cut.centers[1].set(0, -9999, -9999)
    cut.half[0].set(0, 0)
    cut.half[1].set(0, 0)

    if (doorRects[0]) {
      cut.centers[0].copy(doorRects[0].center)
      cut.half[0].copy(doorRects[0].half)
    }
    if (doorRects[1]) {
      cut.centers[1].copy(doorRects[1].center)
      cut.half[1].copy(doorRects[1].half)
    }

    const sh = gateMaterial.userData.shader
    if (sh?.uniforms) {
      sh.uniforms.uDoorCount.value = cut.count
      sh.uniforms.uDoorCenter0.value.copy(cut.centers[0])
      sh.uniforms.uDoorCenter1.value.copy(cut.centers[1])
      sh.uniforms.uDoorHalf0.value.copy(cut.half[0])
      sh.uniforms.uDoorHalf1.value.copy(cut.half[1])
    }

    gateMaterial.needsUpdate = true
  }, [gateMaterial, doorRects])

  // ✅ uniformy (ciemny metal)
  useEffect(() => {
    if (!darkMetalCutMaterial?.userData?.doorCutout) return

    const cut = darkMetalCutMaterial.userData.doorCutout
    cut.count = doorRects.length

    cut.centers[0].set(0, -9999, -9999)
    cut.centers[1].set(0, -9999, -9999)
    cut.half[0].set(0, 0)
    cut.half[1].set(0, 0)

    if (doorRects[0]) {
      cut.centers[0].copy(doorRects[0].center)
      cut.half[0].copy(doorRects[0].half)
    }
    if (doorRects[1]) {
      cut.centers[1].copy(doorRects[1].center)
      cut.half[1].copy(doorRects[1].half)
    }

    const sh = darkMetalCutMaterial.userData.shader
    if (sh?.uniforms) {
      sh.uniforms.uDoorCount.value = cut.count
      sh.uniforms.uDoorCenter0.value.copy(cut.centers[0])
      sh.uniforms.uDoorCenter1.value.copy(cut.centers[1])
      sh.uniforms.uDoorHalf0.value.copy(cut.half[0])
      sh.uniforms.uDoorHalf1.value.copy(cut.half[1])
    }

    darkMetalCutMaterial.needsUpdate = true
  }, [darkMetalCutMaterial, doorRects])

  // ==========================================================
  // ✅ pozycja drzwi w bramie pod DoorSingle - gateSide only
  // ==========================================================
  const computeDoorPositionForDoorSingle = useCallback(
    (door) => {
      const side = resolveDoorSide(door)

      const gateWm = (gate.width || 0) / 100
      const doorWm = (Number(door.width || door.size || 0) || 0) / 100

      const centerOffset = Math.max(0, gateWm / 2 - EDGE_OFFSET_M - doorWm / 2)
      const targetWorldZ = gate.position.z + (side === 2 ? +centerOffset : -centerOffset)

      const targetWorldX = gate.position.x - 0.498 + 0.01 + 0.49
      const targetWorldY = 0

      return {
        x: targetWorldX,
        y: targetWorldY,
        z: -targetWorldZ,
      }
    },
    [gate.position.x, gate.position.z, gate.width, resolveDoorSide]
  )

  return (
    <group ref={wrapperRef} dispose={null}>
      <group
        ref={gateMeshRef}
        wall={'front_wall'}
        position={[gate.position.x, gate.position.y, gate.position.z]}
        castShadow={true}
      >
        <group name="Scene" onClick={handleClick}>
          <mesh
            name="Door_Frame"
            geometry={scaledDoorFrameGeometry}
            material={materials?.['Dark metal.001'] ?? fallbackMaterial}
          />
          <mesh
            name="Door_Left"
            geometry={scaledDoorLeftGeometry}
            material={gateMaterial ?? fallbackMaterial}
            position={[-0.498, gateHeightScale * 0.511, gateWidthScale * -0.484]}
          />
          <group
            name="Door_Right"
            position={[-0.498, gateHeightScale * 0.511, gateWidthScale * 0.486]}
          >
            <mesh
              name="Mesh003"
              geometry={scaledMesh003Geometry}
              material={gateMaterial ?? fallbackMaterial}
            />
            <mesh
              name="Mesh003_1"
              geometry={scaledMesh0031Geometry}
              material={darkMetalCutMaterial ?? (materials?.['Dark metal.001'] ?? fallbackMaterial)}
              position={[0, 0, gateWidthScale * -0.838]}
            />
          </group>
        </group>
      </group>

      {doors.map((door, idx) => {
        const pos = computeDoorPositionForDoorSingle(door)
        const doorForRender = { ...door, position: pos, wall: 'front_wall' }

        return (
          <DoorSingle
            key={`gate-${gate.id}-door-${doorForRender.id ?? idx}`}
            door={doorForRender}
            wall={'front_wall'}
            onUpdate={() => {}}
            textures={textures}
            disableAnimation={false}
            gateTexture={textures?.gates_double_leaf_color}
            {...props}
          />
        )
      })}
    </group>
  )
})

export default DrzwiFramuga

useGLTF.preload('/models/gates/Brama-dwuskrzydlowa-2.glb')
